// Modal for opening an existing (draft | open) ticket into a kiosk tab.

import { useMemo, useState } from 'react';
import { useWbTickets } from '../hooks/useWbTickets';
import { useAuthStore } from '../../../store/authStore';
import styles from './kiosk.module.css';

export interface KioskTicketPickerModalProps {
  readonly onSelect: (encId: string, customerName: string) => void;
  readonly onCancel: () => void;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function KioskTicketPickerModal({ onSelect, onCancel }: KioskTicketPickerModalProps) {
  const { data, isLoading, isError } = useWbTickets(
    { status: ['draft', 'open'], per_page: 100 },
  );
  const currentStaffId = useAuthStore((s) => s.user?.id ?? null);
  const [search, setSearch] = useState('');

  const tickets = data?.tickets ?? [];
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      tickets.filter((t) => {
        if (!q) return true;
        const hay = [t.customer?.name ?? '', t.ticket_number ?? ''].join(' ').toLowerCase();
        return hay.includes(q);
      }),
    [tickets, q],
  );

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Open existing ticket"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
    >
      <div className={styles.modalPanel}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Open Existing Ticket</h2>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onCancel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <input
          type="search"
          className={styles.searchBar}
          placeholder="Search by customer or ticket number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search tickets"
          autoFocus
        />

        {isError ? (
          <div className={styles.error}>Could not load tickets.</div>
        ) : isLoading ? (
          <p className={styles.muted}>Loading…</p>
        ) : filtered.length === 0 ? (
          <p className={styles.muted}>No open or draft tickets found.</p>
        ) : (
          <div className={styles.modalResults}>
            {filtered.map((t) => {
              const lockedByOther =
                t.lock != null && t.lock.locked_by !== currentStaffId ? t.lock : null;
              return (
                <button
                  key={t.enc_id}
                  type="button"
                  className={styles.modalResultRow}
                  onClick={() => onSelect(t.enc_id, t.customer?.name ?? 'Draft')}
                  data-testid={`kiosk-picker-ticket-${t.enc_id}`}
                >
                  <span className={styles.ticketTop}>
                    <span className={styles.modalResultCustomer}>
                      {t.customer?.name ?? <em className={styles.muted}>No customer</em>}
                    </span>
                    {t.ticket_number ? (
                      <span className={styles.ticketNo}> #{t.ticket_number}</span>
                    ) : null}
                    <span
                      className={
                        t.status === 'open'
                          ? `${styles.statusPill} ${styles.statusOpen}`
                          : `${styles.statusPill} ${styles.statusDraft}`
                      }
                    >
                      {t.status}
                    </span>
                    {lockedByOther ? (
                      <span className={styles.ticketLockBadge}>
                        🔒 {lockedByOther.locked_by_name}
                      </span>
                    ) : null}
                    {t.printed_at ? (
                      <span className={styles.printedBadge} title={`Printed ${fmtDate(t.printed_at)}`}>
                        🖨
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.ticketMeta}>
                    {t.yard?.name ?? ''} · {fmtDate(t.created_at)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
